import SessionStorageService from '../../../../core/services/cache/session_storage_service';
import type {CommandSearchResult} from '../types';

const DEFAULT_MAX_RECENT_ITEMS = 8;

export interface StoredRecentResult {
    id: string;
    category: string;
    title: string;
    subtitle?: string;
    badge?: string;
    meta?: string;
    avatarUrl?: string;
    actionKey: string;
    actionPayload: Record<string, string | number | boolean | undefined>;
    visitedAt: number;
}

export interface RecentStoreOptions {
    storageKey?: string;
    maxItems?: number;
}

/** Resolves the session-storage key for recent command-search items. */
const resolveStorageKey = (options: RecentStoreOptions): string => (
    options.storageKey ?? SessionStorageService.keys.panelCommandSearchRecent
);

/** Reads recent command-search selections from session storage. */
export const readRecentResults = (options: RecentStoreOptions): StoredRecentResult[] => {
    if (typeof window === 'undefined') {
        return [];
    }
    const maxItems = options.maxItems ?? DEFAULT_MAX_RECENT_ITEMS;
    try {
        const raw = SessionStorageService.get(resolveStorageKey(options));
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw) as StoredRecentResult[];
        return Array.isArray(parsed) ? parsed.slice(0, maxItems) : [];
    } catch {
        return [];
    }
};

/** Persists a command-search selection for quick recall. */
export const writeRecentResult = (options: RecentStoreOptions, entry: StoredRecentResult): void => {
    if (typeof window === 'undefined') {
        return;
    }
    const maxItems = options.maxItems ?? DEFAULT_MAX_RECENT_ITEMS;
    const existing = readRecentResults(options).filter((item) => item.id !== entry.id);
    const next = [entry, ...existing].slice(0, maxItems);
    SessionStorageService.set(resolveStorageKey(options), JSON.stringify(next));
};

/** Removes one recent entry by stored id. */
export const removeRecentResult = (options: RecentStoreOptions, id: string): void => {
    if (typeof window === 'undefined' || !id) {
        return;
    }
    const next = readRecentResults(options).filter((item) => item.id !== id);
    const key = resolveStorageKey(options);
    if (!next.length) {
        SessionStorageService.remove(key);
        return;
    }
    SessionStorageService.set(key, JSON.stringify(next));
};

/** Clears every recent command-search entry for the given storage key. */
export const clearRecentResults = (options: RecentStoreOptions): void => {
    if (typeof window === 'undefined') {
        return;
    }
    SessionStorageService.remove(resolveStorageKey(options));
};

/** Builds a storable recent entry from a live search result. */
export const toStoredRecentResult = (
    result: CommandSearchResult,
    actionKey: string,
    actionPayload: Record<string, string | number | boolean | undefined>,
): StoredRecentResult => ({
    id: result.id,
    category: result.category,
    title: result.title,
    subtitle: result.subtitle,
    badge: result.badge,
    meta: result.meta,
    avatarUrl: result.avatarUrl,
    actionKey,
    actionPayload,
    visitedAt: Date.now(),
});